<template>
    <div class="p-4 flex-2 z-0">
        <h1 class="text-3xl text-orange-600">
            {{ $t("contact.getInTouch") }}
        </h1>
        <p>{{ $t("contact.subtitle") }}</p>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div class="flex flex-col gap-4">
                <form class="mt-4" @submit.prevent="sendEmail">
                    <div class="grid grid-cols-1 gap-4">
                        <div>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                class="w-full p-2"
                                v-model="form.firstName"
                                :placeholder="`${$t('contact.firstName').toUpperCase()}`"
                            />
                        </div>
                        <div>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                class="w-full p-2"
                                v-model="form.lastName"
                                :placeholder="`${$t('contact.lastName').toUpperCase()}`"
                            />
                        </div>
                        <div>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                class="w-full p-2"
                                v-model="form.email"
                                :placeholder="`${$t('contact.email').toUpperCase()}`"
                            />
                        </div>
                        <div>
                            <textarea
                                id="message"
                                name="message"
                                class="w-full p-2"
                                v-model="form.message"
                                :placeholder="`${$t('contact.message').toUpperCase()}`"
                            ></textarea>
                        </div>
                        <NuxtTurnstile
                            v-model="token"
                            :options="{ theme: 'light' }"
                        />
                        <div>
                            <input
                                class="accent-transparent"
                                type="checkbox"
                                id="privacy"
                                name="privacy"
                                v-model="form.privacy"
                                required
                            />
                            <label for="privacy">
                                {{ $t("contact.agreement") }}
                                <NuxtLink
                                    :to="$localeRoute('/privacy-policy')"
                                    class="font-extrabold underline"
                                >
                                    {{ $t("contact.privacyPolicy") }}
                                </NuxtLink>
                            </label>
                        </div>
                        <div>
                            <button
                                class="bg-orange-500 text-white p-2 rounded-xl float-right"
                                type="submit"
                            >
                                {{ $t("contact.getInTouch") }}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
            <div
                class="flex flex-col gap-4 rounded-2xl p-4 bg-orange-400 text-white"
            >
                <p class="font-extrabold">CARDIOTEAM</p>
                <p>Østgards vei 2</p>
                <p>1718 Greåker</p>
                <p>Sarpsborg</p>
                <p>+47 917 64 213</p>
                <p class="font-semibold">{{ $t("contact.legalDisclaimer") }}</p>
                <p>{{ $t("contact.legalDisclaimerText") }}</p>
            </div>
        </div>

        <!-- Alert Dialog -->
        <div
            v-if="showDialog"
            class="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-50"
        >
            <div class="bg-white rounded-lg p-6 shadow-lg w-96">
                <div
                    v-if="loading"
                    class="flex items-center justify-center gap-2"
                >
                    <div
                        class="loader w-6 h-6 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"
                    ></div>
                    <p class="text-gray-700">
                        {{ $t("contact.sendingEmail") }}
                    </p>
                </div>
                <div v-else>
                    <p class="text-green-600 font-bold">
                        {{ $t("contact.emailSentSuccess") }}
                    </p>
                    <button
                        class="mt-4 bg-orange-500 text-white px-4 py-2 rounded-lg"
                        @click="closeDialog"
                    >
                        {{ $t("contact.closeDialog") }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useHead } from "#imports";

useHead({
    title: "Contact - Cardioteam",
    meta: [
        {
            name: "description",
            content: "Contact Cardioteam",
        },
        {
            name: "keywords",
            content: "Cardioteam, contact",
        },
        {
            name: "author",
            content: "Cardioteam",
        },
        {
            name: "og:title",
            content: "Contact - Cardioteam",
        },
        {
            name: "og:description",
            content: "Contact Cardioteam",
        },
    ],
});

const form = ref({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
    privacy: false, // Checkbox
});

const token = ref("");

const loading = ref(false);
const showDialog = ref(false);

const sendEmail = async () => {
    if (
        !form.value.firstName ||
        !form.value.lastName ||
        !form.value.email ||
        !form.value.message
    ) {
        alert("All fields are required.");
        return;
    }
    if (token.value) {
        const response: any = await $fetch("/api/validateTurnstile", {
            method: "POST",
            body: { token: token.value },
        });

        if (response.success) {
            console.log("Validation success!");
        } else {
            console.error("Validation failed!");
        }
    } else {
        console.error("No token found!");
        alert("Please, prove that you're human!");
        return;
    }

    loading.value = true;
    showDialog.value = true;

    try {
        await $fetch("/api/send-email", {
            method: "POST",
            body: {
                from: form.value.email,
                subject: `${form.value.firstName} ${form.value.lastName} <${form.value.email}>`,
                text: form.value.message,
            },
        });
    } catch (error) {
        console.error("Error:", error);
    } finally {
        loading.value = false;
    }
};

const closeDialog = () => {
    showDialog.value = false;
};
</script>

<style scoped>
.loader {
    display: inline-block;
    border-radius: 50%;
}
</style>
